import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";

import lettreCorpUrl from "../assets/templates/lettre-corp.docx?url";
import lettreUniteUrl from "../assets/templates/lettre-unite.docx?url";
import documentCorpUrl from "../assets/templates/document-corp.docx?url";
import documentUniteUrl from "../assets/templates/document-unite.docx?url";
import excelPortraitUrl from "../assets/templates/excel-portrait.xlsx?url";
import excelPaysageUrl from "../assets/templates/excel-paysage.xlsx?url";
import { markdownToDocxXml, type DocxLook } from "./docxExport";

// Modèles EPFL (lettres, classeurs) que le modèle choisit lui-même (03.11.2026).
// Les fichiers d'origine (models_word_excel/) sont préparés une fois par
// prepare_templates.py : chaque zone à remplir y devient un jeton
// {{clé|texte d'exemple}}, et le corps de la lettre un paragraphe {{corps}}.
// Ici, dans le navigateur, on met dans chaque jeton ce que le modèle a écrit
// dans l'en-tête de son bloc (--- clé: valeur ---), ou le texte d'exemple
// quand il ne le sait pas : l'usager le complète alors dans Word ou Excel.

export interface DocTemplate {
  id: string;
  ext: "docx" | "xlsx";
  /** Nom montré à l'usager sous le nom du fichier. */
  label: string;
  url: string;
}

export const DOC_TEMPLATES: DocTemplate[] = [
  { id: "lettre-corp", ext: "docx", label: "Lettre EPFL, signature École", url: lettreCorpUrl },
  { id: "lettre-unite", ext: "docx", label: "Lettre EPFL, signature unité", url: lettreUniteUrl },
  { id: "document-corp", ext: "docx", label: "Document EPFL, signature École", url: documentCorpUrl },
  { id: "document-unite", ext: "docx", label: "Document EPFL, signature unité", url: documentUniteUrl },
  { id: "excel-portrait", ext: "xlsx", label: "Classeur EPFL A4 portrait", url: excelPortraitUrl },
  { id: "excel-paysage", ext: "xlsx", label: "Classeur EPFL A4 paysage", url: excelPaysageUrl },
];

/** Clé sans accents, en minuscules, mots liés par des tirets. */
export function normalizeKey(key: string): string {
  return key
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-");
}

// Les noms que le modèle peut donner à un champ, vers celui du jeton.
const KEY_ALIASES: Record<string, string> = {
  modele: "modele",
  gabarit: "modele",
  template: "modele",
  objet: "concerne",
  "lieu-et-date": "date",
  adresse: "destinataire",
  auteur: "expediteur",
  signature: "signataire",
  "sous-unite": "sous-unite",
  laboratoire: "sous-unite",
  labo: "sous-unite",
  service: "sous-unite",
  tel: "telephone",
  "e-mail": "email",
  courriel: "email",
  mail: "email",
  "site-web": "site",
  web: "site",
};

export function fieldKey(key: string): string {
  const k = normalizeKey(key);
  return KEY_ALIASES[k] ?? k;
}

export function findTemplate(name: string, ext: string): DocTemplate | null {
  const id = normalizeKey(name).replace(/\.(docx|xlsx)$/, "");
  return DOC_TEMPLATES.find((t) => t.ext === ext && t.id === id) ?? null;
}

export interface TemplateRequest {
  template: DocTemplate;
  /** Champs donnés par le modèle ; une valeur vide efface la ligne du modèle. */
  fields: Map<string, string>;
  /** Le texte à verser dans le modèle (corps de la lettre ou du document). */
  body: string;
}

/** Bloc .docx qui commence par un en-tête « --- modèle: … --- ». Une valeur
 *  peut tenir sur plusieurs lignes : les lignes en retrait la prolongent
 *  (« destinataire: | » puis une ligne d'adresse par ligne). */
export function docxTemplateRequest(markdown: string): TemplateRequest | null {
  const m = /^\s*---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(markdown);
  if (!m) return null;
  const fields = new Map<string, string>();
  let current: string | null = null;
  for (const line of m[1].split(/\r?\n/)) {
    const kv = /^([^\s:][^:]*?)\s*:\s*(.*)$/.exec(line);
    if (kv && !/^\s/.test(line)) {
      current = fieldKey(kv[1]);
      fields.set(current, unquote(kv[2]));
    } else if (current && line.trim()) {
      const prev = fields.get(current) ?? "";
      fields.set(current, prev ? `${prev}\n${unquote(line)}` : unquote(line));
    }
  }
  const template = findTemplate(fields.get("modele") ?? "", "docx");
  if (!template) return null;
  fields.delete("modele");
  return { template, fields, body: markdown.slice(m[0].length) };
}

function unquote(v: string): string {
  const s = v.trim();
  if (s === "|" || s === ">" || s === "|-" || s === ">-") return "";
  return s.replace(/^(["'])(.*)\1$/, "$2");
}

export interface SheetTemplateRequest {
  template: DocTemplate;
  fields: Map<string, string>;
  /** Les données, au format habituel d'un bloc .xlsx (JSON). */
  data: string;
}

/** Bloc .xlsx en JSON avec une clé « modèle » : les données sont sous
 *  « feuilles » ({nom: lignes}) ou « lignes » (une seule feuille). */
export function xlsxTemplateRequest(body: string): SheetTemplateRequest | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body.trim());
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const fields = new Map<string, string>();
  let data: unknown = undefined;
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    const key = fieldKey(k);
    if (key === "feuilles" || key === "lignes" || key === "donnees") data = v;
    else if (typeof v === "string" || typeof v === "number") fields.set(key, String(v));
  }
  const template = findTemplate(fields.get("modele") ?? "", "xlsx");
  if (!template || data === undefined) return null;
  fields.delete("modele");
  return { template, fields, data: JSON.stringify(data) };
}

/** Le fichier du modèle (assets de l'appli). */
export async function loadTemplate(template: DocTemplate): Promise<Uint8Array> {
  const res = await fetch(template.url);
  if (!res.ok) throw new Error(`modèle ${template.id} introuvable (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

const TOKEN_RE = /\{\{([a-z-]+)(?:\|([^}]*))?\}\}/g;

function escXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
}

/** Remplit les jetons d'une partie. `format` met une valeur en XML ; le texte
 *  d'exemple, déjà en XML dans le modèle, passe tel quel. Rend les clés
 *  restées au texte d'exemple. */
function fillTokens(xml: string, fields: Map<string, string>, format: (value: string, isDefault: boolean) => string): { xml: string; defaults: string[] } {
  const defaults: string[] = [];
  const out = xml.replace(TOKEN_RE, (m, key: string, def: string | undefined) => {
    if (key === "corps") return m;
    const value = fields.get(key);
    if (value !== undefined) return format(value, false);
    defaults.push(key);
    return format(def ?? "", true);
  });
  return { xml: out, defaults };
}

/** Une valeur dans un <w:t> : chaque ligne après la première passe à la ligne. */
function wordText(value: string, isDefault: boolean): string {
  return value
    .split("\n")
    .map((l) => (isDefault ? l : escXml(l)))
    .join('</w:t><w:br/><w:t xml:space="preserve">');
}

function attr(xml: string, re: RegExp): number | null {
  const m = re.exec(xml);
  return m ? Number(m[1]) : null;
}

/** Le paragraphe qui contient `at`. */
function paragraphAround(xml: string, at: number): [number, number] {
  const start = Math.max(xml.lastIndexOf("<w:p>", at), xml.lastIndexOf("<w:p ", at));
  const end = xml.indexOf("</w:p>", at) + "</w:p>".length;
  return [start, end];
}

export interface FilledTemplate {
  bytes: Uint8Array;
  /** Champs restés au texte d'exemple, à compléter par l'usager. */
  defaults: string[];
}

/** La lettre ou le document : champs dans le corps, les en-têtes et pieds de page, texte
 *  rédigé à la place du paragraphe {{corps}}, aux styles PolyChatCorps et
 *  PolyChatTitre du modèle. */
export function fillDocxTemplate(template: Uint8Array, fields: Map<string, string>, markdown: string): FilledTemplate {
  const files = unzipSync(template);
  const defaults = new Set<string>();
  // Les champs d'abord : le texte rédigé n'est pas relu à la recherche de jetons.
  for (const name of Object.keys(files)) {
    if (!/^word\/(document|header\d*|footer\d*)\.xml$/.test(name)) continue;
    const filled = fillTokens(strFromU8(files[name]), fields, wordText);
    files[name] = strToU8(filled.xml);
    filled.defaults.forEach((k) => defaults.add(k));
  }

  let doc = strFromU8(files["word/document.xml"]);
  const at = doc.indexOf("{{corps}}");
  if (at >= 0) {
    const styles = strFromU8(files["word/styles.xml"]);
    const corpsStyle = /<w:style [^>]*w:styleId="PolyChatCorps"[\s\S]*?<\/w:style>/.exec(styles)?.[0] ?? "";
    const indent = attr(corpsStyle, /<w:ind [^>]*w:left="(\d+)"/) ?? 0;
    const pageWidth = attr(doc, /<w:pgSz [^>]*w:w="(\d+)"/) ?? 11906;
    const margins = (attr(doc, /<w:pgMar [^>]*w:left="(\d+)"/) ?? 1134) + (attr(doc, /<w:pgMar [^>]*w:right="(\d+)"/) ?? 1134);
    const look: DocxLook = {
      body: "PolyChatCorps",
      indent,
      tableWidth: pageWidth - margins - indent,
      styles: {
        // # → Heading2, ## → Heading3, ### et au-delà → Heading4 (docxExport).
        Heading1: "PolyChatTitre1",
        Heading2: "PolyChatTitre1",
        Heading3: "PolyChatTitre2",
        Heading4: "PolyChatTitre",
        Quote: "PolyChatCorps",
        Code: "PolyChatCorps",
        Rule: "PolyChatCorps",
      },
    };
    const { xml, links } = markdownToDocxXml(markdown.trim(), look);
    const [start, end] = paragraphAround(doc, at);
    doc = doc.slice(0, start) + xml + doc.slice(end);
    if (links.length > 0) {
      const relsName = "word/_rels/document.xml.rels";
      const rels = strFromU8(files[relsName]);
      const added = links
        .map((l) => `<Relationship Id="${l.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${escXml(l.target)}" TargetMode="External"/>`)
        .join("");
      files[relsName] = strToU8(rels.replace("</Relationships>", `${added}</Relationships>`));
    }
    files["word/document.xml"] = strToU8(doc);
  }
  return { bytes: zipSync(files, { level: 6 }), defaults: [...defaults] };
}

/** Une valeur dans un code d'en-tête Excel : « & » s'y double, une seule ligne. */
export function sheetHeaderText(value: string, isDefault: boolean): string {
  if (isDefault) return value;
  return escXml(value.replace(/\s*\n\s*/g, " – ").replace(/&/g, "&&"));
}

/** En-tête et pied de page d'une feuille, champs remplis. */
export function fillSheetFields(xml: string, fields: Map<string, string>): { xml: string; defaults: string[] } {
  return fillTokens(xml, fields, sheetHeaderText);
}

/** Texte d'exemple des champs, tel que le modèle l'affiche : pour l'aperçu,
 *  qui ne charge pas le fichier du modèle. */
export function fieldExample(template: DocTemplate, key: string): string {
  if (template.id.endsWith("-corp") && key === "unite") return "Institut (niveau 3)";
  if (template.id.endsWith("-corp") && key === "sous-unite") return "Labo (niveau 4)";
  return FIELD_EXAMPLES[key] ?? "";
}

const FIELD_EXAMPLES: Record<string, string> = {
  destinataire: "Entreprise\nPrénom Nom\nAdresse\nCH-1015 Lausanne",
  date: "Lausanne, le XX mois 2019",
  concerne: "Concerne",
  salutation: "Chère Madame, cher Monsieur,",
  signataire: "Titre, prénom, nom",
  unite: "Nom de l'unité",
  "sous-unite": "Service ou laboratoire",
  faculte: "Faculté (niveau 2)",
  sigle: "EPFL XXX XXX",
  expediteur: "Prénom Nom",
  bureau: "XX XX XX Adresse",
  station: "Station N° XX",
  telephone: "+4121 693 XX XX",
  email: "xx@epfl.ch",
  site: "www.epfl.ch",
  titre: "Titre du document",
};

/** Champs que l'usager complétera, dits comme dans l'en-tête du bloc. */
export const FIELD_LABELS: Record<string, string> = {
  destinataire: "destinataire",
  date: "date",
  concerne: "objet",
  salutation: "salutation",
  signataire: "signataire",
  faculte: "faculté",
  unite: "unité",
  "sous-unite": "sous-unité",
  sigle: "sigle de l'unité",
  expediteur: "expéditeur",
  bureau: "bureau",
  station: "station",
  telephone: "téléphone",
  email: "e-mail",
  site: "site web",
  titre: "titre",
};

export function missingFieldsNote(defaults: string[]): string {
  return defaults.map((k) => FIELD_LABELS[k] ?? k).join(", ");
}

/** Les modèles « document » n'ont pas de bloc de signature : quand le modèle
 *  en donne une (« signe au nom de… »), elle suit le texte, une ligne par
 *  paragraphe (un saut de ligne dans un paragraphe justifié étirerait le nom). */
export function withSignature(template: DocTemplate, model: Map<string, string>, fields: Map<string, string>, body: string): string {
  if (!template.id.startsWith("document-") || !model.get("signataire")?.trim()) return body;
  const lines = (fields.get("signataire") ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return body;
  // Le modèle a souvent déjà signé à la fin du texte (son nom, sa fonction,
  // parfois un nom écrit autrement) : ces dernières lignes cèdent la place à
  // la signature complète, plutôt que de la répéter.
  const plain = (l: string) => l.replace(/[*_`>#\s]+/g, " ").replace(/\u00A0/g, " ").trim().toLowerCase();
  const signature = new Set(lines.map(plain));
  const nameWords = new Set(plain(lines[0]).split(" "));
  // « Anna Fontcuberta » pour « Anna Fontcuberta i Morral » : un nom abrégé.
  const shortName = (l: string) => l.split(" ").length >= 2 && l.split(" ").every((w) => nameWords.has(w));
  const text = body.trimEnd().split("\n");
  while (text.length > 0) {
    const last = plain(text[text.length - 1]);
    if (last === "" || last === "&nbsp;" || signature.has(last) || shortName(last)) text.pop();
    else break;
  }
  // Une ligne vide avant la signature : une espace insécable seule dans son
  // paragraphe (une entité « &nbsp; » resterait écrite telle quelle dans Word).
  return `${text.join("\n").trimEnd()}\n\n\u00A0\n\n${lines.join("\n\n")}\n`;
}
